import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { fetchAdminAnalytics } from "../../apis/DashboardApis";
import { useAuth } from "../Authentication/AuthContext";
import { SkeletonAnalytics } from "../Skeletons";
import {
  ChartCard,
  SimpleAreaChart,
  MultiAreaChart,
  SimpleBarChart,
  DonutChart,
  StatCard,
  CHART_COLORS,
  GRADIENT_COLORS,
} from "../charts/ChartComponents";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export default function AdminAnalyticsTab() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [dateRange, setDateRange] = useState("30"); // days

  // Load consolidated analytics data from single API endpoint
  useEffect(() => {
    const loadData = async () => {
      if (!user?.token) {
        setAnalyticsData(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await fetchAdminAnalytics(user.token, { dateRange, months: 6 });
        
        // Handle different response formats
        if (response?.success && response?.data) {
          setAnalyticsData(response.data);
        } else if (response?.data) {
          setAnalyticsData(response.data);
        } else {
          // Direct response format
          setAnalyticsData(response);
        }
      } catch (error) {
        console.error("Error loading analytics data:", error);
        setAnalyticsData(null);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user?.token, dateRange]);

  // Extract data from consolidated API response (pre-computed by backend)
  const summary = analyticsData?.summary || {};
  const totalApplications = summary.totalApplies ?? summary.totalApplications ?? 0;
  const totalLikes = summary.totalLikes ?? summary.totalJobLikes ?? 0;
  const jobTypeData = (analyticsData?.jobTypeDistribution || []).map((item, idx) => ({
    ...item,
    color: item.color || GRADIENT_COLORS[idx % GRADIENT_COLORS.length],
  }));
  const jobStatusData = (analyticsData?.jobStatusDistribution || []).map((item) => ({
    ...item,
    color: item.name === "Active" ? CHART_COLORS.success : CHART_COLORS.danger,
  }));
  const jobsByMonth = analyticsData?.jobsByMonth || [];
  const applicationsData = analyticsData?.applicationsByMonth || [];
  const usersByMonth = analyticsData?.usersByMonth || [];
  const topCompanies = analyticsData?.topCompanies || [];
  const experienceData = (analyticsData?.experienceDistribution || []).map((item, idx) => ({
    ...item,
    color: item.color || GRADIENT_COLORS[idx % GRADIENT_COLORS.length],
  }));
  const recentJobs = analyticsData?.recentJobs || [];

  // Sparkline data generator
  const generateSparkData = (data, key) => 
    data.map((item) => ({ value: item[key] || 0 }));

  if (loading) {
    return <SkeletonAnalytics />;
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Analytics Overview</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Track your platform's performance and growth</p>
        </div>
        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          className="ca-input w-auto text-sm"
        >
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="365">Last year</option>
        </select>
      </motion.div>

      {/* Stat Cards - values come pre-computed from API */}
      <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Jobs"
          value={(summary.totalJobs || 0).toLocaleString()}
          change={summary.jobsChange || "+0%"}
          changeType={summary.jobsChangeType || "increase"}
          icon="ri-briefcase-line"
          color="blue"
          sparkData={generateSparkData(jobsByMonth, "jobs")}
        />
        <StatCard
          title="Total Users"
          value={(summary.totalUsers || 0).toLocaleString()}
          change={summary.usersChange || "+0%"}
          changeType={summary.usersChangeType || "increase"}
          icon="ri-user-line"
          color="purple"
          sparkData={generateSparkData(usersByMonth, "users")}
        />
        <StatCard
          title="Applications"
          value={totalApplications.toLocaleString()}
          change={summary.applicationsChange || "+0%"}
          changeType={summary.applicationsChangeType || "increase"}
          icon="ri-send-plane-line"
          color="green"
          sparkData={generateSparkData(applicationsData, "applications")}
        />
        <StatCard
          title="Total Likes"
          value={totalLikes.toLocaleString()}
          change={summary.likesChange || "+0%"}
          changeType={summary.likesChangeType || "increase"}
          icon="ri-heart-line"
          color="red"
          sparkData={generateSparkData(applicationsData, "likes")}
        />
      </motion.div>

      {/* Charts Row 1 */}
      <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Jobs Posted Over Time" subtitle="Monthly job posting trends">
          <SimpleAreaChart data={jobsByMonth} dataKey="jobs" color={CHART_COLORS.primary} height={280} />
        </ChartCard>

        <ChartCard title="User Registrations" subtitle="Monthly user growth">
          <SimpleAreaChart data={usersByMonth} dataKey="users" color={CHART_COLORS.secondary} height={280} />
        </ChartCard>
      </motion.div>

      {/* Charts Row 2 */}
      <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ChartCard title="Job Type Distribution" subtitle="Distribution by category">
          <DonutChart 
            data={jobTypeData} 
            height={280} 
            centerLabel="Total"
            centerValue={summary.totalJobs || 0}
          />
        </ChartCard>

        <ChartCard title="Job Status" subtitle="Active vs Expired">
          <DonutChart 
            data={jobStatusData} 
            height={280}
            centerLabel="Active"
            centerValue={jobStatusData.find(d => d.name === "Active")?.value || 0}
          />
        </ChartCard>

        <ChartCard title="Experience Level" subtitle="Required experience distribution">
          <DonutChart 
            data={experienceData} 
            height={280}
            centerLabel="Levels"
            centerValue={experienceData.length}
          />
        </ChartCard>
      </motion.div>

      {/* Charts Row 3 */}
      <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Applications & Likes" subtitle="User engagement over time">
          <MultiAreaChart
            data={applicationsData}
            lines={[
              { dataKey: "applications", name: "Applications", color: CHART_COLORS.primary },
              { dataKey: "likes", name: "Likes", color: CHART_COLORS.danger },
            ]}
            height={280}
          />
        </ChartCard>

        <ChartCard title="Top Companies" subtitle="Companies with most job postings">
          <SimpleBarChart data={topCompanies} dataKey="jobs" xAxisKey="name" height={280} />
        </ChartCard>
      </motion.div>

      {/* Recent Activity - pre-computed list from API */}
      <motion.div variants={itemVariants}>
        <ChartCard title="Recent Job Posts" subtitle="Latest job listings on the platform">
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {recentJobs.length > 0 ? (
              recentJobs.map((job, idx) => (
                <div
                  key={job.id || idx}
                  className="flex items-center gap-4 p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <img
                    src={job.companyLogo}
                    alt={job.companyName}
                    className="w-10 h-10 rounded-lg object-contain bg-white dark:bg-gray-600 border border-gray-200 dark:border-gray-600"
                    onError={(e) => {
                      e.target.src = `https://ui-avatars.com/api/?name=${job.companyName}&background=random`;
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">{job.title}</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{job.companyName}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    job.type === "job" 
                      ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" 
                      : job.type === "internship"
                      ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
                      : "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                  }`}>
                    {job.type}
                  </span>
                  <div className="text-right">
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-heart-line text-red-400"></i>
                        {job.likesCount || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-send-plane-line text-blue-400"></i>
                        {job.appliesCount || 0}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <i className="ri-briefcase-line text-4xl mb-2 block opacity-50"></i>
                <p>No recent jobs to display</p>
              </div>
            )}
          </div>
        </ChartCard>
      </motion.div>
    </motion.div>
  );
}
